import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useMatches, useNavigate} from 'react-router-dom';

import {ownerKey, rememberGuideSeen} from '../components/learn/state/learnMemory';
import {useGuidesAccess, useUserModules} from '../context/UserModulesContext';
import {AccessService} from '../utils/AccessService';
import {LocalStorage} from '../utils/LocalStorage';
import {isOk} from '../utils/apiResponse';
import {isSuperAdmin as isSuperAdminFromStorage} from '../utils/FeatureFlags';
import {isEmbedded, isSuppressedPath} from '../utils/floatingWidgets';
import {
    GUIDE_FROM_LEARN,
    GUIDE_FROM_PARAM,
    GUIDE_TASK_PARAM,
    LEARN_PATH,
    LEARN_RETURN_PARAM,
    MODE_AUTO,
    MODE_MANUAL,
    MODE_PREVIEW,
    REFETCH_ON_FOCUS_MS,
    RETURN_POST_WAIT_MS,
    SCREEN_SETTLE_MS,
    SESSION_SCREEN_BY_GUIDE_STORAGE_KEY,
    SESSION_SCREENS_STORAGE_KEY,
    SESSION_STORAGE_KEY,
    SNOOZE_DAYS,
    STEP_PERSIST_DEBOUNCE_MS,
    APP_WARMUP_MS,
} from './constants';
import {anchorDrawable} from './anchors';
import {GuidesService} from './GuidesService';
import {GuidesTracker} from './GuidesTracker';
import {
    autoBlockers,
    isFirstVisitGuide,
    isOptionalStep,
    isWaitingElsewhere,
    nextDrawableIndex,
    pickAutoGuide,
    previousShownIndex,
    stepRunnableOn,
} from './orchestrator';
import {guideScreenKey} from './screens';

/**
 * Espera el guardado del cierre de una guía antes de volver a Aprende, con tope: la página decide con
 * el estado del backend qué recorrido falta, pero con la red lenta volver importa más.
 */
function afterClosing(closing) {
    const posted = Promise.resolve(closing).catch(() => null);
    const capped = new Promise((resolve) => setTimeout(resolve, RETURN_POST_WAIT_MS));
    return Promise.race([posted, capped]);
}

/** A dónde se vuelve: la página, con la tarea de la que se viene para que abra la que sigue. */
export function learnReturnPath(returnTo) {
    const task = returnTo && returnTo.task;
    return task ? `${LEARN_PATH}?${LEARN_RETURN_PARAM}=${encodeURIComponent(task)}` : LEARN_PATH;
}

/**
 * Estado de las Guías in-app para toda la app. Va en `App.js` porque es lo único que no remonta al
 * navegar (el marco 2.1 remonta en cada pantalla): una guía que cruza pantallas tiene que sobrevivir
 * a la navegación, y el tope «una automática por sesión» también.
 *
 * Tres maneras de arrancar una guía:
 *   - AUTO: el orquestador la dispara al entrar a la pantalla de disparo (con todo el ritmo de §7).
 *   - MANUAL: `?guide=<slug>` en la URL o `useGuides().start(slug)` («Mostrame cómo»). Ignora la
 *     sesión y el cooldown, y NO reabre una guía que el usuario ya cerró (lo garantiza el Backend).
 *   - PREVIEW: `?guide_preview=<id>`, sólo superadmins, desde el editor del Command Center. No
 *     persiste ni mide nada.
 *
 * Una guía MANUAL lanzada desde «Aprende Dashbo» (`?guide_from=aprende&guide_task=<tarea>`) lleva
 * `returnTo`: al terminarla o cerrarla queda ofrecido volver a la página con la tarea que sigue
 * abierta (`returnOffer`, lo dibuja `GuideReturnBar`), y el último paso que no avanza con un clic en la
 * pantalla dice «Volver a Aprende» y vuelve directo (`returnToLearn`). C-03 del recorrido con Pablo.
 */

const GuidesContext = createContext({
    active: null,
    screen: null,
    uiVersion: 'v1',
    frame: 'v1',
    start: () => {},
    next: () => {},
    prev: () => {},
    complete: () => {},
    dismiss: () => {},
    snooze: () => {},
    close: () => {},
    skipStep: () => {},
    markDisplayed: () => {},
    trackStepViewed: () => {},
    trackCta: () => {},
    releaseScreen: () => {},
    returnOffer: null,
    returnToLearn: () => {},
    goBackToLearn: () => {},
    dismissReturnOffer: () => {},
});

// El momento en que cargó la app. Módulo y no estado: el provider puede remontar (login sin
// recarga) y el calentamiento es de la pestaña, no del componente.
const APP_STARTED_AT = Date.now();

// Los topes de la sesión van por USUARIO (review C-rec-guias-07): si A cierra sesión y B entra en la
// misma pestaña, B no hereda las pantallas que A ya usó.
function sessionKey(base, userId) {
    return `${base}_u${userId}`;
}

function readAutoShownCount(userId) {
    try {
        return Number(sessionStorage.getItem(sessionKey(SESSION_STORAGE_KEY, userId))) || 0;
    } catch (_) {
        return 0;
    }
}

function bumpAutoShownCount(userId) {
    try {
        sessionStorage.setItem(sessionKey(SESSION_STORAGE_KEY, userId), String(readAutoShownCount(userId) + 1));
    } catch (_) { /* modo incógnito estricto: el tope por sesión se pierde, nada más */ }
}

// Una automática por PANTALLA por sesión (contrato §3 de la tanda 2026-10-02-recorrido-ux21): es el
// tope que también frena a los recorridos de primera visita, para no encimar dos guías en la misma
// pantalla. Guarda la clave de `guideScreenKey`, así el Tablero por `/` y por `/marketing` es uno.
function readAutoScreens(userId) {
    try {
        const screens = JSON.parse(sessionStorage.getItem(sessionKey(SESSION_SCREENS_STORAGE_KEY, userId)) || '[]');
        return Array.isArray(screens) ? screens : [];
    } catch (_) {
        return [];
    }
}

function markAutoScreen(userId, screenKey) {
    if (!screenKey) return;
    try {
        const screens = readAutoScreens(userId);
        if (!screens.includes(screenKey)) {
            sessionStorage.setItem(sessionKey(SESSION_SCREENS_STORAGE_KEY, userId),
                JSON.stringify([...screens, screenKey]));
        }
    } catch (_) { /* sin sessionStorage el tope por pantalla se pierde, nada más */ }
}

function unmarkAutoScreen(userId, screenKey) {
    if (!screenKey) return;
    try {
        const screens = readAutoScreens(userId);
        if (screens.includes(screenKey)) {
            sessionStorage.setItem(sessionKey(SESSION_SCREENS_STORAGE_KEY, userId),
                JSON.stringify(screens.filter((key) => key !== screenKey)));
        }
    } catch (_) { /* sin sessionStorage no había tope que liberar */ }
}

// Qué pantalla gastó cada guía automática (`{guideId: screenKey}`, por usuario y pestaña). Sobrevive al
// F5 igual que la marca de la pantalla, así que la reapertura manual de esa guía sabe qué liberar.
function readScreenByGuide(userId) {
    try {
        const map = JSON.parse(sessionStorage.getItem(sessionKey(SESSION_SCREEN_BY_GUIDE_STORAGE_KEY, userId)) || '{}');
        return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
    } catch (_) {
        return {};
    }
}

function rememberGuideScreen(userId, guideId, screenKey) {
    if (!screenKey || guideId === undefined || guideId === null) return;
    try {
        sessionStorage.setItem(sessionKey(SESSION_SCREEN_BY_GUIDE_STORAGE_KEY, userId),
            JSON.stringify({...readScreenByGuide(userId), [guideId]: screenKey}));
    } catch (_) { /* sin sessionStorage no hay tope que recordar */ }
}

/** Saca y devuelve la pantalla que gastó esta guía, o null si no gastó ninguna. */
function takeGuideScreen(userId, guideId) {
    const map = readScreenByGuide(userId);
    const screenKey = map[guideId] || null;
    if (!screenKey) return null;
    try {
        delete map[guideId];
        sessionStorage.setItem(sessionKey(SESSION_SCREEN_BY_GUIDE_STORAGE_KEY, userId), JSON.stringify(map));
    } catch (_) { /* idem */ }
    return screenKey;
}

// Capas que tapan la pantalla y no ponen `body.modal-open`: con cualquiera abierta, nada se
// dispara solo (review C-GUIAS-10). El panel del perfil del cliente, el cajón «Tus primeros pasos» y
// el cajón de edición de una línea.
const OPEN_LAYER_SELECTORS = '.client-profile-side-panel, .sd-scrim';
const OPEN_LAYER_BODY_CLASSES = ['modal-open', 'line-edit-drawer-open'];

function anyLayerOpen() {
    if (OPEN_LAYER_BODY_CLASSES.some((cls) => document.body.classList.contains(cls))) return true;
    return !!document.querySelector(OPEN_LAYER_SELECTORS);
}

function currentScreenOf(matches) {
    const last = matches && matches.length ? matches[matches.length - 1] : null;
    return (last && last.handle && last.handle.screen) || null;
}

export function GuidesProvider({children}) {
    const matches = useMatches();
    const location = useLocation();
    const navigate = useNavigate();
    const {enabled, loaded, uiVersion, isSuperAdmin} = useGuidesAccess();
    const {modules, alertRulesEnabled} = useUserModules();
    // `/` es el Tablero sólo con el módulo de marketing (`Default.js`): sin él, el recorrido del
    // Tablero no puede salir ahí (review C-rec-guias-05). Ver `screens.js`.
    const dashboardIsTablero = Array.isArray(modules) ? modules.includes(AccessService.MODULE_MARKETING) : true;
    // «Inversión sin control» es una solapa del Centro sólo con el Centro; sin él se ve sola y la guía del
    // Centro no puede arrancar ahí (UD-5, `screens.js`). Es `alertRulesVisible` (el flag Y la 2.1) escrito
    // acá: los tests del provider mockean el contexto sin ese helper.
    const alertsCenter = alertRulesEnabled === true && uiVersion === 'v2';
    const frame = useMemo(() => ({uiVersion, dashboardIsTablero, alertsCenter}),
        [uiVersion, dashboardIsTablero, alertsCenter]);

    const screen = currentScreenOf(matches);
    const screenKey = guideScreenKey(screen, frame);
    const userId = LocalStorage.fetchUserId() || null;
    const canRun = !!userId && !LocalStorage.isClientUser() && !isEmbedded();

    const [eligible, setEligible] = useState([]);
    const [cooldownUntil, setCooldownUntil] = useState(null);
    // {guide, index, mode, skipped, returnTo}: `skipped` son los índices que se saltearon en esta
    // corrida, para que «Anterior» no vuelva a ellos y el «x de y» no los cuente. `returnTo` ({task}):
    // la lanzó «Aprende Dashbo».
    const [active, setActive] = useState(null);
    // Volver a Aprende, ofrecido después de terminar o cerrar una guía que vino de ahí. Sobrevive a la
    // navegación (el provider no remonta): el último paso suele ser el botón real, que abre un modal o
    // un cajón, y la vuelta se ofrece cuando la persona termina lo suyo.
    const [returnOffer, setReturnOffer] = useState(null);
    // Quién pidió la guía: una respuesta de `by-slug` que vuelve después de un cambio de cuenta en la
    // misma pestaña (el login es SPA) no se le muestra a la cuenta nueva (review C-rp-fe-aprende-07).
    const userIdRef = useRef(userId);
    userIdRef.current = userId;
    const fetchedAtRef = useRef(0);
    const persistTimerRef = useRef(null);
    const activeRef = useRef(null);
    activeRef.current = active;
    // Recorridos de primera visita que no se pudieron dibujar en ESTA visita a la pantalla (ver
    // `skipStep`): no se reintentan hasta que el usuario cambie de pantalla.
    const deferredRef = useRef(new Set());

    useEffect(() => {
        deferredRef.current = new Set();
    }, [screenKey]);

    // --- Carga de las elegibles ------------------------------------------------------------ //

    const refresh = useCallback(() => {
        GuidesService.fetchEligible(uiVersion).then((response) => {
            if (!isOk(response)) return; // 404 = gate cerrado o sin agencia: no hay nada que mostrar
            fetchedAtRef.current = Date.now();
            setEligible(Array.isArray(response.guides) ? response.guides : []);
            setCooldownUntil(response.auto_cooldown_until || null);
        });
    }, [uiVersion]);

    useEffect(() => {
        setEligible([]);
        setActive(null);
        if (!canRun || !loaded || !enabled) return;
        refresh();
    }, [userId, canRun, loaded, enabled, refresh]);

    // La vuelta ofrecida es de quien hizo la guía: otra cuenta en la misma pestaña no la hereda.
    useEffect(() => {
        setReturnOffer(null);
    }, [userId]);

    useEffect(() => {
        if (!canRun || !enabled) return undefined;
        const onFocus = () => {
            if (Date.now() - fetchedAtRef.current > REFETCH_ON_FOCUS_MS) refresh();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [canRun, enabled, refresh]);

    // --- Persistencia ---------------------------------------------------------------------- //

    // Devuelve el pedido: volver a Aprende lo espera, porque la página decide qué recorrido falta con el
    // estado de las guías que tiene el backend.
    const postState = useCallback((current, status, index, extra = {}) => {
        if (!current || current.mode === MODE_PREVIEW) return Promise.resolve(null);
        return GuidesService.postState(current.guide.id, {
            version: current.guide.version,
            status,
            current_step: index,
            auto: current.mode === MODE_AUTO,
            screen: screen || '',
            ui: uiVersion,
            ...extra,
        });
    }, [screen, uiVersion]);

    const track = useCallback((current, type, stepIndex) => {
        if (!current || current.mode === MODE_PREVIEW) return;
        GuidesTracker.track({
            guide_id: current.guide.id,
            version: current.guide.version,
            type,
            step_index: stepIndex,
            screen: screen || '',
            ui: uiVersion,
        });
    }, [screen, uiVersion]);

    const forget = useCallback((guideId) => {
        setEligible((list) => list.filter((g) => g.id !== guideId));
    }, []);

    // --- Acciones -------------------------------------------------------------------------- //

    // ¿Se llegó a DIBUJAR algo de la guía activa? Hasta que no, nada se persiste ni se mide como
    // vista: una guía cuyo primer paso nunca aparece (ancla ausente, otra pantalla, otro marco) no
    // puede gastar el cupo de la sesión ni el cooldown de 48 h, ni quedar «completada» o «vista» para
    // un usuario que no vio nada (review C-GUIAS-01). Es un ref y no estado: lo leen callbacks que ya
    // se ejecutaron con el `active` de ese momento.
    const displayedRef = useRef(false);

    const begin = useCallback((guide, index, mode, {returnTo = null} = {}) => {
        if (!guide || !Array.isArray(guide.steps) || guide.steps.length === 0) return;
        clearTimeout(persistTimerRef.current);
        displayedRef.current = false;
        setReturnOffer(null);
        setActive({guide, index, mode, skipped: [], returnTo: mode === MODE_MANUAL ? returnTo : null});
    }, []);

    /** Lo llama el runner cuando joyride pinta el punto pulsante o la tarjeta. */
    const markDisplayed = useCallback(() => {
        const current = activeRef.current;
        if (!current || displayedRef.current) return;
        displayedRef.current = true;
        // La guía de una tarea de Aprende cuenta como vista recién cuando se DIBUJA: el botón de la tarea
        // deja de ofrecerla después de la primera vez (C-04), y una que nunca llegó (la red, un ancla que
        // no apareció) no puede gastarla (review C-rp-fe-aprende-01).
        if (current.returnTo && current.returnTo.task) {
            rememberGuideSeen(ownerKey(userId, LocalStorage.fetchAgencyId()), current.returnTo.task);
        }
        if (current.mode === MODE_AUTO) {
            // Un recorrido de primera visita también cuenta para el cupo de las demás: no lo mira
            // él, pero después de verlo la guía común espera a otra sesión.
            bumpAutoShownCount(userId);
            markAutoScreen(userId, screenKey);
            rememberGuideScreen(userId, current.guide.id, screenKey);
        }
        postState(current, 'shown', current.index, {display_start: true});
    }, [postState, userId, screenKey]);

    /** Cierra la guía sólo en este cliente: no persiste nada y la saca de las elegibles de la pestaña. */
    const dropLocally = useCallback(() => {
        const current = activeRef.current;
        clearTimeout(persistTimerRef.current);
        if (current) forget(current.guide.id);
        setActive(null);
    }, [forget]);

    // Terminada o cerrada una guía que vino de Aprende, queda ofrecido volver (salvo que vuelva ya:
    // `offerReturn: false`). Una que nunca se dibujó no ofrece nada: no pasó nada de qué volver.
    const finish = useCallback((status, extra = {}, {offerReturn = true} = {}) => {
        const current = activeRef.current;
        if (!current) return Promise.resolve(null);
        if (!displayedRef.current) {
            dropLocally();
            return Promise.resolve(null);
        }
        clearTimeout(persistTimerRef.current);
        const posted = postState(current, status, current.index, extra);
        forget(current.guide.id);
        setActive(null);
        // La oferta lleva el guardado del cierre: volver por la barra también lo espera (C-…-04).
        if (offerReturn && current.returnTo) setReturnOffer({...current.returnTo, closing: posted});
        return posted;
    }, [postState, forget, dropLocally]);

    // Sin devolver el pedido de `finish`: los botones de la tarjeta no lo esperan (sólo `returnToLearn`).
    const complete = useCallback(() => { finish('completed'); }, [finish]);
    const dismiss = useCallback(() => { finish('dismissed'); }, [finish]);
    const snooze = useCallback((days = SNOOZE_DAYS) => { finish('snoozed', {snooze_days: days}); }, [finish]);
    const close = useCallback(() => setActive(null), []);

    /**
     * «Volver a Aprende» desde el último paso: la completa y vuelve a la página. Espera (con tope) a
     * que el backend guarde el cierre: la página decide con él qué recorrido falta y qué tarea abrir.
     */
    const returnToLearn = useCallback(() => {
        const current = activeRef.current;
        const target = learnReturnPath(current && current.returnTo);
        afterClosing(finish('completed', {}, {offerReturn: false})).then(() => navigate(target));
    }, [finish, navigate]);

    // La barra: el cierre casi siempre ya se guardó, pero si se aprieta enseguida, la página decidiría
    // con la tarea todavía pendiente (y el recorrido relanzaría el que se acaba de terminar).
    const goBackToLearn = useCallback(() => {
        const target = learnReturnPath(returnOffer);
        const closing = returnOffer && returnOffer.closing;
        setReturnOffer(null);
        afterClosing(closing).then(() => navigate(target));
    }, [returnOffer, navigate]);

    const dismissReturnOffer = useCallback(() => setReturnOffer(null), []);

    // En la página, la oferta ya se cumplió (por la barra, por el menú o por el lanzador).
    useEffect(() => {
        if (location.pathname === LEARN_PATH) setReturnOffer(null);
    }, [location.pathname]);

    const goTo = useCallback((index, skipped) => {
        const current = activeRef.current;
        if (!current) return;
        // El paso al que se llega deja de contar como salteado: si un opcional que faltó aparece
        // después y se muestra, el «x de y» y «Anterior» lo cuentan (review C-rec-guias-07).
        const stillSkipped = [...new Set(skipped || current.skipped || [])].filter((i) => i !== index);
        const updated = {...current, index, skipped: stillSkipped};
        setActive(updated);
        clearTimeout(persistTimerRef.current);
        if (!displayedRef.current) return;
        persistTimerRef.current = setTimeout(
            () => postState(updated, 'shown', index), STEP_PERSIST_DEBOUNCE_MS);
    }, [postState]);

    /**
     * Desde `from`, el próximo paso que se puede dibujar: los opcionales de esta pantalla cuyo
     * elemento no está se saltean YA (con la pantalla cargada, esperar 1,5 s por cada uno eran
     * huecos mudos). Devuelve el índice y la lista de salteados actualizada.
     */
    const advanceFrom = useCallback((current, from, alsoSkipped = []) => {
        const steps = current.guide.steps;
        const target = nextDrawableIndex(steps, from, {screen, frame, isAnchorDrawable: anchorDrawable});
        const jumped = [];
        for (let i = from; i < target; i += 1) jumped.push(i);
        return {target, skipped: [...(current.skipped || []), ...alsoSkipped, ...jumped]};
    }, [screen, frame]);

    /**
     * `{lookahead: false}` = ir al paso siguiente sin saltear nada: lo usa el avance por clic en el
     * elemento, que corre en captura ANTES del handler del clic, así que lo que ese clic despliega
     * todavía no está en el DOM (review C-rec-guias-06). El runner espera a ese paso como a cualquiera.
     * Los botones de la tarjeta la llaman con el evento de React, que no trae `lookahead`.
     */
    const next = useCallback((options) => {
        const current = activeRef.current;
        if (!current) return;
        const lookahead = !(options && options.lookahead === false);
        const {target, skipped} = lookahead
            ? advanceFrom(current, current.index + 1)
            : {target: current.index + 1, skipped: current.skipped};
        // Lo que quedaba era opcional y no aplica: el usuario vio todo lo que había para ver.
        if (target >= current.guide.steps.length) {
            complete();
        } else {
            goTo(target, skipped);
        }
    }, [advanceFrom, complete, goTo]);

    const prev = useCallback(() => {
        const current = activeRef.current;
        if (!current) return;
        const target = previousShownIndex(current.index, current.skipped);
        if (target !== null) goTo(target);
    }, [goTo]);

    /**
     * Saltar un paso que NO se puede dibujar no es avanzar (review C-GUIAS-01):
     *   - `anchor_missing`: el ancla no apareció a tiempo. Se mide (el CC lo muestra como «ancla
     *     rota»).
     *   - `ui`: el ancla es de la otra versión del marco. No se mide: no está rota (C-GUIAS-02).
     * Si era el último paso, la guía se cierra SÓLO en el cliente, sin `completed`: quien no llegó a
     * ver ese paso no la completó, y el embudo no puede premiar a las guías rotas.
     *
     * Un paso OPCIONAL (`step.optional`) es la excepción: que su elemento no esté no es un ancla rota
     * sino que no hay datos. Se saltea sin medir y sin aplazar el recorrido, y si era lo último que
     * quedaba y algo ya se vio, la guía SÍ se completa.
     */
    const skipStep = useCallback((reason) => {
        const current = activeRef.current;
        if (!current) return;
        const optional = isOptionalStep(current.guide.steps[current.index]);
        const deferFirstVisit = current.mode === MODE_AUTO && isFirstVisitGuide(current.guide)
            && !displayedRef.current;
        const defer = () => {
            deferredRef.current.add(current.guide.id);
            clearTimeout(persistTimerRef.current);
            setActive(null);
        };
        // Un recorrido de primera visita que no pudo dibujar NADA todavía no sigue con los pasos que
        // queden: la pantalla no está como la espera (la vista Economía de Clientes, `/` sin el
        // Tablero, un Panel que no cargó). Se aplaza hasta otra visita a la pantalla, sin medir un
        // «ancla rota» que no lo es y sin gastar nada (review C-rec-guias-05/06).
        if (deferFirstVisit && !optional) {
            defer();
            return;
        }
        if (reason === 'anchor_missing' && !optional) track(current, 'anchor_missing', current.index);
        const {target, skipped} = advanceFrom(current, current.index + 1, [current.index]);
        const steps = current.guide.steps;
        // Sin nada dibujado, un recorrido de primera visita que pasaría a esperar en OTRA pantalla se
        // aplaza como con un paso fijo: si no, el disparo lo volvía a elegir cada pocos segundos
        // (nada lo frena mientras nunca se vio; review C-rec-guias-08).
        if (deferFirstVisit && target < steps.length && !stepRunnableOn(steps[target], screen, frame)) {
            defer();
            return;
        }
        if (target < steps.length) {
            goTo(target, skipped);
        } else if (optional && displayedRef.current) {
            complete();
        } else if (deferFirstVisit) {
            defer(); // todos los pasos eran opcionales y ninguno aplica todavía: otra visita
        } else {
            dropLocally();
        }
    }, [track, goTo, dropLocally, advanceFrom, complete, screen, frame]);

    const trackStepViewed = useCallback(() => {
        const current = activeRef.current;
        if (current) track(current, 'step_viewed', current.index);
    }, [track]);

    const trackCta = useCallback(() => {
        const current = activeRef.current;
        if (current) track(current, 'cta_clicked', current.index);
    }, [track]);

    /**
     * El CTA de una guía que lleva a otra pantalla de la app libera, del tope «una automática por
     * pantalla por sesión», la pantalla que ESA guía gastó al dibujarse en automático: el usuario pidió
     * seguir, así que no hay nada que encimar. Es lo que deja arrancar el recorrido de primera visita
     * después del modal de bienvenida aunque el modal haya salido en la misma pantalla.
     *
     * La pantalla sale de lo que recordó `markDisplayed`, no del render actual (review
     * C-…-renovado-01): el modal sin pantalla sigue al usuario si cambia de ruta, y la guía puede
     * estar reabierta en MANUAL desde el menú después de cerrarla con la × — la pantalla que gastó en
     * automático sigue tomada y es ésa la que hay que soltar. Nunca en la vista previa, y nunca la
     * marca de OTRA guía: si ésta no gastó ninguna, no hace nada.
     */
    const releaseScreen = useCallback(() => {
        const current = activeRef.current;
        if (!current || current.mode === MODE_PREVIEW) return;
        unmarkAutoScreen(userId, takeGuideScreen(userId, current.guide.id));
    }, [userId]);

    /**
     * Arranque manual. Si había otra guía en curso, la reemplaza: su estado ya quedó guardado. Arranca
     * apenas llega la guía: el calentamiento de la app y el asentamiento de la pantalla son del disparo
     * AUTOMÁTICO (H-16: quien pidió ayuda la recibe ya). Lo que queda es esperar su ancla.
     */
    const start = useCallback((slug, options) => {
        const askedBy = userIdRef.current;
        GuidesService.fetchBySlug(slug).then((response) => {
            if (userIdRef.current !== askedBy) return;
            if (isOk(response) && response.guide) begin(response.guide, 0, MODE_MANUAL, options);
        });
    }, [begin]);

    // --- Arranque desde la URL (?guide= / ?guide_preview=) ----------------------------------- //

    useEffect(() => {
        if (!canRun || !loaded) return;
        const params = new URLSearchParams(location.search);
        const slug = params.get('guide');
        const previewId = params.get('guide_preview');
        if (!slug && !previewId) return;
        const fromLearn = params.get(GUIDE_FROM_PARAM) === GUIDE_FROM_LEARN;
        const returnTo = fromLearn ? {task: params.get(GUIDE_TASK_PARAM) || null} : null;

        params.delete('guide');
        params.delete('guide_preview');
        params.delete(GUIDE_FROM_PARAM);
        params.delete(GUIDE_TASK_PARAM);
        const search = params.toString();
        navigate({pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash},
            {replace: true});

        if (previewId && (isSuperAdmin || isSuperAdminFromStorage())) {
            GuidesService.fetchPreview(previewId).then((response) => {
                if (isOk(response) && response.guide) begin(response.guide, 0, MODE_PREVIEW);
            });
        } else if (slug && enabled) {
            start(slug, {returnTo});
        }
    }, [location.search, canRun, loaded, enabled, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

    // --- Disparo automático ---------------------------------------------------------------- //

    useEffect(() => {
        if (!canRun || !enabled || eligible.length === 0 || !screen) return undefined;
        // Una guía automática que quedó esperando en OTRA pantalla no tapa los recorridos de primera
        // visita de ésta: si no, quien dejó el de Clientes a medias no veía el del Panel en toda la
        // sesión. Si arranca uno, la reemplaza; su estado ya quedó guardado y la retoma otra sesión.
        if (active && !isWaitingElsewhere(active, screen, frame)) return undefined;
        if (isSuppressedPath(location.pathname)) return undefined;

        // Se espera lo que falte del calentamiento de la app o el asentamiento de la pantalla, lo que
        // sea mayor; las condiciones se evalúan RECIÉN al vencer (un modal pudo abrirse mientras tanto).
        const warmupLeft = Math.max(0, APP_STARTED_AT + APP_WARMUP_MS - Date.now());
        const timer = setTimeout(() => {
            const current = activeRef.current;
            if (current && !isWaitingElsewhere(current, screen, frame)) return;
            const blockers = autoBlockers({
                hasActiveGuide: !!current,
                activeWaitingElsewhere: !!current,
                autoShownThisSession: readAutoShownCount(userId),
                cooldownUntil,
                screenUsedThisSession: readAutoScreens(userId).includes(screenKey),
                now: Date.now(),
                appStartedAt: APP_STARTED_AT,
                modalOpen: anyLayerOpen(),
                viewportWidth: window.innerWidth,
                pathname: window.location.pathname,
            });
            const candidates = eligible.filter((guide) => !deferredRef.current.has(guide.id));
            const picked = pickAutoGuide({eligible: candidates, screen, frame, blockers});
            if (picked.guide) begin(picked.guide, picked.index, MODE_AUTO);
        }, Math.max(SCREEN_SETTLE_MS, warmupLeft));
        return () => clearTimeout(timer);
    }, [screen, screenKey, frame, userId, eligible, cooldownUntil, active, canRun, enabled, location.pathname, begin]);

    useEffect(() => () => clearTimeout(persistTimerRef.current), []);

    const value = useMemo(() => ({
        active,
        screen,
        uiVersion,
        frame,
        start,
        next,
        prev,
        complete,
        dismiss,
        snooze,
        close,
        skipStep,
        markDisplayed,
        trackStepViewed,
        trackCta,
        releaseScreen,
        returnOffer,
        returnToLearn,
        goBackToLearn,
        dismissReturnOffer,
    }), [active, screen, uiVersion, frame, start, next, prev, complete, dismiss, snooze, close,
        skipStep, markDisplayed, trackStepViewed, trackCta, releaseScreen, returnOffer, returnToLearn,
        goBackToLearn, dismissReturnOffer]);

    return <GuidesContext.Provider value={value}>{children}</GuidesContext.Provider>;
}

/** Para arrancar una guía desde el producto: `useGuides().start('informes-nuevo')`. */
export const useGuides = () => useContext(GuidesContext);
